import * as React from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Boton,
  Campo,
  Dialog,
  DialogContenido,
  DialogDescripcion,
  DialogDisparador,
  DialogEncabezado,
  DialogPie,
  DialogTitulo,
  EsqueletoTabla,
  Input,
  Pill,
  PuntoNaranja,
  Tabla,
  TablaCabecera,
  TablaCelda,
  TablaCuerpo,
  TablaEncabezado,
  TablaFila,
  Textarea,
  TituloPagina,
  useToast,
  Vacio,
} from "@iavmx/ui";
import { leer } from "../api";
import { Candado, Cifra, ErrorEnLinea, FilaTiempo, FranjaEtapas, Miga, PillsAlerta, Seccion } from "../componentes";
import { useNombresProducto, useYo } from "../datos";
import { fechaCorta, hoyIso, mxn, relativo } from "../formato";
import { useSesion } from "../sesion";
import { ETAPAS, ETIQUETA_ESTADO_COTIZACION, ETIQUETA_ESTADO_PROPUESTA, ETIQUETA_ETAPA, type Etapa, type RespuestaOportunidad } from "../tipos";
import { CamposProximaAccion } from "./Cuenta";

const TIPOS = [
  ["llamada", "Llamada"],
  ["visita", "Visita"],
  ["reunion", "Reunión"],
  ["correo", "Correo"],
  ["whatsapp", "WhatsApp"],
  ["tarea", "Tarea"],
] as const;

function RegistrarActividad({ id, onListo }: { id: string; onListo: () => void }) {
  const { api } = useSesion();
  const { avisar } = useToast();
  const [abierto, setAbierto] = React.useState(false);
  const [tipo, setTipo] = React.useState<(typeof TIPOS)[number][0]>("llamada");
  const [resumen, setResumen] = React.useState("");
  const [resultado, setResultado] = React.useState("");
  const [accion, setAccion] = React.useState("");
  const [fecha, setFecha] = React.useState(hoyIso(2));
  const m = useMutation({
    mutationFn: () => api.api.oportunidades[":id"].actividades.$post({ param: { id }, json: { tipo, resumen: resumen.trim(), resultado: resultado.trim() || null, proximaAccion: accion, proximaAccionFecha: fecha } }).then((r) => leer<{ id: string; odoo: string }>(r)),
    onSuccess: (r) => {
      avisar({ titulo: "Actividad registrada", descripcion: r.odoo === "escrita" ? "También quedó escrita en Odoo." : r.odoo === "sin_id" ? "Odoo conectado, pero esta oportunidad no viene de ahí." : "Odoo sin conectar: se guardó solo aquí.", tono: "exito" });
      setAbierto(false);
      setResumen("");
      setResultado("");
      setAccion("");
      onListo();
    },
  });
  const valida = resumen.trim().length >= 2 && accion.trim().length >= 3 && fecha;
  return (
    <Dialog open={abierto} onOpenChange={setAbierto}>
      <DialogDisparador asChild>
        <Boton variante="acento" tamano="sm">
          Registrar actividad
        </Boton>
      </DialogDisparador>
      <DialogContenido className="max-h-[90vh] overflow-y-auto">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valida) m.mutate();
          }}
        >
          <DialogEncabezado>
            <DialogTitulo>Registrar actividad</DialogTitulo>
            <DialogDescripcion>Lo que hiciste hoy y lo que sigue. Si Odoo está conectado, la actividad se escribe allá.</DialogDescripcion>
          </DialogEncabezado>
          <fieldset>
            <legend className="mb-1.5 block font-ui text-xs font-semibold uppercase tracking-wide text-tinta-3">Tipo</legend>
            <div className="flex flex-wrap gap-1.5">
              {TIPOS.map(([v, t]) => (
                <label key={v} className={`inline-flex h-8 cursor-pointer items-center rounded-full border px-3 font-ui text-xs font-semibold ${tipo === v ? "border-tinta bg-tinta text-tinta-inversa" : "border-linea-fuerte text-tinta-2"}`}>
                  <input type="radio" name="tipo" className="sr-only" checked={tipo === v} onChange={() => setTipo(v)} />
                  {t}
                </label>
              ))}
            </div>
          </fieldset>
          <Campo etiqueta="Qué pasó" htmlFor="ar">
            <Input id="ar" value={resumen} onChange={(e) => setResumen(e.target.value)} placeholder="Reunión con Ana Ruiz y Héctor Salinas" required autoFocus className="text-base sm:text-sm" />
          </Campo>
          <Campo etiqueta="Resultado" htmlFor="ares" ayuda="Opcional. Presupuesto, objeciones, acuerdos.">
            <Textarea id="ares" value={resultado} onChange={(e) => setResultado(e.target.value)} className="min-h-16 text-base sm:text-sm" />
          </Campo>
          <CamposProximaAccion accion={accion} fecha={fecha} onAccion={setAccion} onFecha={setFecha} />
          <ErrorEnLinea error={m.error} />
          <DialogPie>
            <Boton type="button" variante="contorno" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" disabled={!valida || m.isPending}>
              {m.isPending ? "Guardando…" : "Guardar actividad"}
            </Boton>
          </DialogPie>
        </form>
      </DialogContenido>
    </Dialog>
  );
}

function MoverEtapa({ id, etapa, onListo }: { id: string; etapa: Etapa; onListo: () => void }) {
  const { api } = useSesion();
  const { avisar } = useToast();
  const [abierto, setAbierto] = React.useState(false);
  const [nueva, setNueva] = React.useState<Etapa>(etapa);
  const [accion, setAccion] = React.useState("");
  const [fecha, setFecha] = React.useState(hoyIso(2));
  const [motivo, setMotivo] = React.useState("");
  const m = useMutation({
    mutationFn: () => api.api.oportunidades[":id"].etapa.$post({ param: { id }, json: { etapa: nueva, proximaAccion: accion, proximaAccionFecha: fecha, motivoPerdida: motivo || null } }).then((r) => leer<{ ok: boolean }>(r)),
    onSuccess: () => {
      avisar({ titulo: `Ahora está en ${ETIQUETA_ETAPA[nueva].toLowerCase()}`, tono: "exito" });
      setAbierto(false);
      onListo();
    },
  });
  const valida = nueva !== etapa && accion.trim().length >= 3 && fecha && (nueva !== "perdido" || motivo.trim());
  const final = etapa === "ganado";
  return (
    <Dialog open={abierto} onOpenChange={setAbierto}>
      <DialogDisparador asChild>
        <Boton variante="contorno" tamano="sm" disabled={final} title={final ? "Una oportunidad ganada no se mueve; crea una renovación." : undefined}>
          Mover de etapa
        </Boton>
      </DialogDisparador>
      <DialogContenido>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valida) m.mutate();
          }}
        >
          <DialogEncabezado>
            <DialogTitulo>Mover de etapa</DialogTitulo>
            <DialogDescripcion>Cada cambio de etapa pide su próxima acción. Perdido pide el motivo; ganado es final.</DialogDescripcion>
          </DialogEncabezado>
          <fieldset>
            <legend className="mb-1.5 block font-ui text-xs font-semibold uppercase tracking-wide text-tinta-3">Etapa nueva</legend>
            <div className="flex flex-wrap gap-1.5">
              {ETAPAS.map((e) => (
                <label key={e} className={`inline-flex h-8 cursor-pointer items-center rounded-full border px-3 font-ui text-xs font-semibold ${nueva === e ? "border-tinta bg-tinta text-tinta-inversa" : "border-linea-fuerte text-tinta-2"} ${e === etapa ? "opacity-50" : ""}`}>
                  <input type="radio" name="etapa" className="sr-only" checked={nueva === e} onChange={() => setNueva(e)} disabled={e === etapa} />
                  {ETIQUETA_ETAPA[e]}
                </label>
              ))}
            </div>
          </fieldset>
          {nueva === "perdido" && (
            <Campo etiqueta="Motivo de la pérdida" htmlFor="mp">
              <Input id="mp" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Presupuesto, tiempos, competencia…" required className="text-base sm:text-sm" />
            </Campo>
          )}
          <CamposProximaAccion accion={accion} fecha={fecha} onAccion={setAccion} onFecha={setFecha} ayuda={nueva === "ganado" ? "Por ejemplo: pasar a facturación (SIA)." : nueva === "perdido" ? "Por ejemplo: recontactar en enero." : undefined} />
          <ErrorEnLinea error={m.error} />
          <DialogPie>
            <Boton type="button" variante="contorno" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" disabled={!valida || m.isPending}>
              {m.isPending ? "Moviendo…" : `Mover a ${ETIQUETA_ETAPA[nueva].toLowerCase()}`}
            </Boton>
          </DialogPie>
        </form>
      </DialogContenido>
    </Dialog>
  );
}

export function Oportunidad() {
  const { id } = useParams({ from: "/oportunidades/$id" });
  const { api, listo } = useSesion();
  const qc = useQueryClient();
  const { avisar } = useToast();
  const yo = useYo();
  const nombreDe = useNombresProducto();
  const q = useQuery({ queryKey: ["oportunidad", id], queryFn: () => api.api.oportunidades[":id"].$get({ param: { id } }).then((r) => leer<RespuestaOportunidad>(r)), enabled: listo });
  const recargar = () => {
    void qc.invalidateQueries({ queryKey: ["oportunidad", id] });
    void qc.invalidateQueries({ queryKey: ["oportunidades"] });
  };
  const [accion, setAccion] = React.useState<string | null>(null);
  const [fecha, setFecha] = React.useState<string | null>(null);
  const guardarAccion = useMutation({
    mutationFn: () => api.api.oportunidades[":id"].$patch({ param: { id }, json: { proximaAccion: accion ?? undefined, proximaAccionFecha: fecha ?? undefined } }).then((r) => leer<{ ok: boolean }>(r)),
    onSuccess: () => {
      avisar({ titulo: "Próxima acción guardada", tono: "exito" });
      setAccion(null);
      setFecha(null);
      recargar();
    },
  });
  const escalar = useMutation({
    mutationFn: () => api.api.oportunidades[":id"].escalar.$post({ param: { id } }).then((r) => leer<{ ok: boolean; dias: number }>(r)),
    onSuccess: (r) => avisar({ titulo: "Alerta enviada", descripcion: `Se avisó al ejecutivo y a su jefe (${r.dias} días sin actividad).`, tono: "exito" }),
  });

  if (q.isLoading) return <EsqueletoTabla />;
  if (q.error || !q.data) return <Vacio titulo="No se pudo abrir la oportunidad" texto={q.error?.message} />;
  const { oportunidad: o, actividades, cotizaciones, propuestas, contactos, odoo, config } = q.data;
  const a = accion ?? o.proximaAccion;
  const f = fecha ?? o.proximaAccionFecha;
  const cambio = a !== o.proximaAccion || f !== o.proximaAccionFecha;
  const abierta = !["ganado", "perdido"].includes(o.etapa);
  const gerencia = yo.data ? ["admin", "direccion", "gerencia"].includes(yo.data.rol) : false;
  const propia = yo.data?.id === o.ejecutivoId;

  return (
    <>
      <Miga partes={[{ texto: "Pipeline", to: "/" }, { texto: ETIQUETA_ETAPA[o.etapa] }, { texto: o.cuenta, to: "/cuentas/$id", params: { id: o.cuentaId } }]} />
      <TituloPagina
        descripcion={`${o.nombre}${o.productos.length ? ` · ${o.productos.map(nombreDe).join(", ")}` : ""}`}
        accion={
          <div className="flex flex-wrap gap-2">
            <MoverEtapa id={o.id} etapa={o.etapa} onListo={recargar} />
            {abierta && <RegistrarActividad id={o.id} onListo={recargar} />}
          </div>
        }
      >
        {o.cuenta}
        <PuntoNaranja />
      </TituloPagina>

      <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Cifra valor={o.montoVisible ? (o.valor ? mxn(o.valor) : "—") : <Candado texto="no visible" />} leyenda="Valor" />
        <Cifra valor={o.fechaCierreEstimada ? fechaCorta(o.fechaCierreEstimada) : "—"} leyenda="Cierre estimado" />
        <Cifra valor={o.probabilidad != null ? `${o.probabilidad} %` : "—"} leyenda="Probabilidad" />
        <Cifra valor={<span className="text-base">{o.ejecutivo ?? "Sin asignar"}</span>} leyenda="Ejecutivo" />
      </div>
      <FranjaEtapas etapa={o.etapa} compacta />
      <div className="mt-3">
        <PillsAlerta alertas={o.alertas} etapa={o.etapa} altoValor={o.altoValor} />
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-8">
          {abierta && (
            <section className="border border-linea-fuerte p-4">
              <form
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (cambio) guardarAccion.mutate();
                }}
              >
                <CamposProximaAccion accion={a} fecha={f} onAccion={setAccion} onFecha={setFecha} ayuda={o.alertas.vencida ? "La fecha ya pasó: registra la actividad o pon una fecha nueva." : `Sin actividad ${o.alertas.diasSinActividad} días. A los ${config.diasInactividad} se marca detenida; alto valor escala a dirección a los ${config.diasEscalamiento}.`} />
                <ErrorEnLinea error={guardarAccion.error} />
                <div className="flex flex-wrap items-center gap-2">
                  <Boton type="submit" tamano="sm" disabled={!cambio || guardarAccion.isPending}>
                    {guardarAccion.isPending ? "Guardando…" : "Guardar próxima acción"}
                  </Boton>
                  {(o.alertas.detenida || o.alertas.vencida) && (gerencia || propia) && (
                    <Boton type="button" tamano="sm" variante="liga" onClick={() => escalar.mutate()} disabled={escalar.isPending}>
                      Mandar la alerta ahora
                    </Boton>
                  )}
                </div>
              </form>
            </section>
          )}

          <Seccion titulo="Actividades">
            {actividades.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Sin actividades. Registra la primera llamada o reunión.</p>
            ) : (
              <ul>
                {actividades.map((x) => (
                  <FilaTiempo key={x.id} titulo={x.resumen ?? x.tipo} detalle={[x.ejecutivo, x.resultado, x.escritaEnOdooEn ? "escrita en Odoo" : null].filter(Boolean).join(" · ")} en={x.fecha} azul={x.automatica} />
                ))}
              </ul>
            )}
          </Seccion>

          <Seccion
            titulo="Cotizaciones"
            accion={
              (gerencia || propia) && abierta ? (
                <Boton variante="liga" tamano="sm" asChild>
                  <Link to="/cotizaciones/nueva" search={{ cuenta: o.cuentaId, oportunidad: o.id }}>
                    Nueva cotización
                  </Link>
                </Boton>
              ) : undefined
            }
          >
            {!o.montoVisible ? (
              <Candado texto={`Las cotizaciones de ${o.ejecutivo ?? "otro ejecutivo"} solo las ve su dueño y la gerencia.`} />
            ) : cotizaciones.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Sin cotizaciones. La primera se arma desde el catálogo.</p>
            ) : (
              <Tabla>
                <TablaCabecera>
                  <TablaFila>
                    <TablaEncabezado>Folio</TablaEncabezado>
                    <TablaEncabezado className="text-right">Descuento</TablaEncabezado>
                    <TablaEncabezado className="text-right">Total</TablaEncabezado>
                    <TablaEncabezado>Estado</TablaEncabezado>
                  </TablaFila>
                </TablaCabecera>
                <TablaCuerpo>
                  {cotizaciones.map((c) => (
                    <TablaFila key={c.id}>
                      <TablaCelda>
                        <Link to="/cotizaciones/$id" params={{ id: c.id }} className="font-semibold hover:underline">
                          COT-{String(c.folio).padStart(4, "0")}
                        </Link>
                      </TablaCelda>
                      <TablaCelda className="text-right tabular-nums">{Number(c.descuentoPct)} %</TablaCelda>
                      <TablaCelda className="text-right tabular-nums">{mxn(c.total)}</TablaCelda>
                      <TablaCelda>{ETIQUETA_ESTADO_COTIZACION[c.estado]}</TablaCelda>
                    </TablaFila>
                  ))}
                </TablaCuerpo>
              </Tabla>
            )}
            {o.montoVisible && <p className="font-ui text-xs text-tinta-tenue">Solo tú y la gerencia ven estas cotizaciones.</p>}
          </Seccion>
        </div>

        <aside className="min-w-0 space-y-8">
          <Seccion titulo="Propuesta viva">
            {propuestas.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Sin propuesta. Se crea desde una cotización con «Convertir en propuesta».</p>
            ) : (
              <ul className="space-y-3">
                {propuestas.map((p) => (
                  <li key={p.id} className="border border-linea p-3 font-ui text-sm">
                    <Pill tono={p.estado === "aceptada" ? "verde" : p.estado === "cambios_pedidos" ? "ambar" : "azul"}>{ETIQUETA_ESTADO_PROPUESTA[p.estado]}</Pill>
                    <div className="mt-2 font-semibold">{p.titulo}</div>
                    <div className="text-xs text-tinta-tenue">
                      {p.enviadaEn ? `Enviada ${relativo(p.enviadaEn)}` : "Sin enviar"}
                      {p.primeraAperturaEn ? ` · abierta ${relativo(p.primeraAperturaEn)}` : ""}
                    </div>
                    <Link to="/propuestas/$id" params={{ id: p.id }} className="mt-2 inline-block text-azul hover:underline">
                      Ver rastreo completo
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Seccion>
          <Seccion titulo="Contactos">
            {contactos.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Sin contactos en la cuenta.</p>
            ) : (
              <ul className="space-y-2 font-ui text-sm">
                {contactos.map((c) => (
                  <li key={c.id}>
                    <span className="font-semibold">{c.nombre}</span>
                    <span className="text-tinta-tenue"> · {[c.puesto, c.telefono].filter(Boolean).join(" · ")}</span>
                  </li>
                ))}
              </ul>
            )}
          </Seccion>
          <Seccion titulo="Odoo">
            <p className="font-ui text-sm text-tinta-3">
              {odoo.conectado
                ? o.odooId
                  ? `Oportunidad #${o.odooId}. Las actividades que registres aquí se escriben en Odoo.`
                  : "Conectado, pero esta oportunidad nació aquí y no existe en Odoo."
                : "Odoo sin conectar: las actividades se guardan solo en esta plataforma. Cuando se conecte (plan Custom de Odoo), se sincroniza cada 15 minutos."}
            </p>
          </Seccion>
        </aside>
      </div>
    </>
  );
}
